// Batch body format: many files in one request or response, so small files are not sent
// one request each. Also spoken by the Worker (worker/src/blobbatch.ts) and the backend
// (backend/blobbatch.py): change all three together.
//
//   u32 LE header length | JSON header [{"k": key, "n": size}, ...] | the blobs, back to back

/**
 * A request or response carries about this many bytes and at most this many files; a file of that size or more
 * goes alone. One file per request, because the Worker has to handle the bytes of a batch in JavaScript and
 * Workers Free allows only 10 ms of CPU per request. Raise it (together with ABVME_BATCH_ITEMS in the backend)
 * only on Workers Paid.
 */
export const BATCH_BYTES = 16 * 1024 * 1024;
export const BATCH_ITEMS = 1;

/** Group items in order into batches of about `target` bytes and at most `maxItems` items. */
export function planBatches(items, sizeOf, target = BATCH_BYTES, maxItems = BATCH_ITEMS) {
  const batches = [];
  let current = [];
  let used = 0;
  for (const item of items) {
    const size = Math.max(0, sizeOf(item));
    if (size >= target) {
      if (current.length) batches.push(current);
      current = [];
      used = 0;
      batches.push([item]);
      continue;
    }
    if (current.length && (used + size > target || current.length >= maxItems)) {
      batches.push(current);
      current = [];
      used = 0;
    }
    current.push(item);
    used += size;
  }
  if (current.length) batches.push(current);
  return batches;
}

/** entries: [{ k, blob }]. A Blob keeps File objects as references, so nothing is copied into memory. */
export function encodeBatch(entries) {
  const header = new TextEncoder().encode(JSON.stringify(entries.map((e) => ({ k: e.k, n: e.blob.size }))));
  const length = new Uint8Array(4);
  new DataView(length.buffer).setUint32(0, header.length, true);
  return new Blob([length, header, ...entries.map((e) => e.blob)]);
}

/** Split a batch into [{ k, data: ArrayBuffer }]. */
export function decodeBatch(buffer) {
  const view = new DataView(buffer);
  const length = view.getUint32(0, true);
  const header = JSON.parse(new TextDecoder().decode(new Uint8Array(buffer, 4, length)));
  let offset = 4 + length;
  return header.map(({ k, n }) => {
    if (offset + n > buffer.byteLength) throw new Error("The server sent an incomplete response");
    const data = buffer.slice(offset, offset + n);
    offset += n;
    return { k, data };
  });
}
